import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import {
  careDurationOptionSchema,
  routableCareCallTypeSchema,
  tolerantRoutableCareCallTypeSchema,
} from '../care/care-call-types';
import {
  callReasonAllowedForCallType,
  careCallReasonSchema,
  isImportOnlyCareCallReason,
  progressPurposeReasonSchema,
  tolerantCareCallReasonSchema,
} from '../care/care-call-reason';
import { careBookingPurposeSchema } from '../care/care-booking-purpose';
import { nextCallDataSchema, nextCallResponseSchema } from '../care/care-call-types';
import { apiEnvelope } from '../envelope';
import { ClinicianRole } from '../programs/schema';
import { conversionAttributionSchema } from '../attribution/schema';
import { careTeamPhotoUrlShape } from '../care/care-photo-url';
import type { CareAppointmentStoredSource } from '../care/appointments';
import { careAppointmentModalitySchema } from '../care/appointments';
import { isModalityAllowed } from '../care/care-call-types';

export { nextCallDataSchema, nextCallResponseSchema };
export type { NextCallData, NextCallResponse } from '../care/care-call-types';

// Wire schemas for the member scheduling surface (`/api/v1/care/appointments*`).
// Member identity is resolved server-side from the bearer JWT — no request
// shape carries a member/identity field. This module is re-exported from the
// shared barrel the mobile bundle consumes, so it MUST NOT transitively import
// `../db/*`.

/**
 * Booking origin — a safe analytics dimension distinguishing the Care-tab
 * sheet, the in-chat `book_care_team_call` client tool, and a staff booking
 * made from Atlas. Validated enum, never an authorisation input.
 */
export const careAppointmentSourceSchema = z.enum([
  'member-sheet',
  'agent-tool',
  'staff-atlas',
  'healthie-calendar',
]);
export type CareAppointmentSource = z.infer<typeof careAppointmentSourceSchema>;

/** Rotation vs explicit specialist target — a safe analytics dimension only. */
export const careAppointmentAssignmentModeSchema = z.enum(['explicit', 'round_robin']);
export type CareAppointmentAssignmentMode = z.infer<typeof careAppointmentAssignmentModeSchema>;

// The wire enum is a deliberate SUBSET of the stored vocabulary: the migration
// writes a fourth value clients never see, because `source` is not projected
// onto any client read — an imported booking appears in the member's list
// like any other, without its provenance. This assertion is what keeps the
// subset honest — a stored value renamed without renaming it here stops
// compiling.
const _wireSourcesAreStoredSources: readonly CareAppointmentStoredSource[] =
  careAppointmentSourceSchema.options;
void _wireSourcesAreStoredSources;

/**
 * Booking origins a MEMBER request may carry — `staff-atlas` is server-set on
 * the staff booking path only, so the member wire schema stays narrowed.
 */
export const memberCareAppointmentSourceSchema = z.enum(['member-sheet', 'agent-tool']);
export type MemberCareAppointmentSource = z.infer<typeof memberCareAppointmentSourceSchema>;

export const careAppointmentStatusSchema = z.enum(['booked', 'cancelled', 'completed']);
export type CareAppointmentStatus = z.infer<typeof careAppointmentStatusSchema>;

/**
 * Response for `GET /api/v1/care/appointments/slots`: the member's bookable
 * instants, ISO-8601 UTC, ascending. For a member with a primary care
 * specialist these are that specialist's free instants (a follow-up call);
 * otherwise the union across the active specialist pool. An empty grid yields
 * `slots: []` (200) — absence of capacity is a state, not an error. Clients
 * group instants for the member's device timezone. `specialistName` names the
 * person the member will meet whenever exactly one specialist can be promised
 * (their primary, or a pool that resolves to one specialist); `null` when the
 * pool is empty or plural — rotation assignment means no individual can be
 * promised, and clients fall back to generic copy.
 */
// ADR-135: response tolerance — unknown keys are stripped, never rejected.
export const careAppointmentSlotsDataSchema = z.object({
  slots: z.array(z.string().datetime({ offset: true })),
  durationMinutes: z.number().int().positive(),
  specialistName: z.string().min(1).nullable(),
  /** The call type these slots book, as the server derives it; absent from older servers. */
  callType: tolerantRoutableCareCallTypeSchema,
  /**
   * The lengths this call type offers (member reads list only member-selectable
   * ones), ascending. Absent from older servers; a client shows a length picker
   * only when more than one is listed.
   */
  durationOptions: z.array(careDurationOptionSchema).optional(),
  /**
   * Bookable instants per listed length, keyed by minutes as a string. Each
   * longer length's instants are a subset of every shorter length's, and
   * `slots` equals the entry for the applied `durationMinutes`.
   */
  slotsByDuration: z.record(z.string(), z.array(z.string().datetime({ offset: true }))).optional(),
});
export type CareAppointmentSlotsData = z.infer<typeof careAppointmentSlotsDataSchema>;

export const careAppointmentSlotsResponseSchema = apiEnvelope(careAppointmentSlotsDataSchema);
export type CareAppointmentSlotsResponse = z.infer<typeof careAppointmentSlotsResponseSchema>;

/** Optional length on a slot-read query string; coerced from its text form. */
const durationMinutesQuerySchema = z.coerce.number().int().positive().optional();

/** Optional length (minutes) on a booking or reschedule body; absent means the default. */
const durationMinutesBodySchema = z.number().int().positive().optional();

/** Optional query for member slot reads scoped to one specialist or to one booked call. */
export const careAppointmentSlotsQuerySchema = z
  .object({
    specialistId: z.string().uuid().optional(),
    /**
     * The member's booked call being moved: slots come from that call's own
     * specialist and call type, with its current slot left free. Takes
     * precedence over `specialistId`.
     */
    rescheduleAppointmentId: z.string().uuid().optional(),
    /** Length to apply (minutes); absent means the call type's default, or the booked length on reschedule. */
    durationMinutes: durationMinutesQuerySchema,
    /**
     * A booking path other than the member's next derived call. The server
     * picks the pool and call type for it, so it cannot be combined with a
     * specialist or a call being moved.
     */
    purpose: careBookingPurposeSchema.optional(),
  })
  .strict()
  .refine(
    (query) =>
      query.purpose === undefined ||
      (query.specialistId === undefined && query.rescheduleAppointmentId === undefined),
    { message: 'purpose cannot be combined with specialistId or rescheduleAppointmentId' },
  );
export type CareAppointmentSlotsQuery = z.infer<typeof careAppointmentSlotsQuerySchema>;

/**
 * Request body for `POST /api/v1/care/appointments`. A call booked is a
 * designated conversion (ADR-132): the device's live landing capture rides
 * the write as `attribution`. Optional — an unattributed booking is the
 * common case — and validated here so a malformed pair is a 400, never a
 * silently dropped analytics dimension.
 */
export const bookCareAppointmentRequestSchema = z
  .object({
    startsAt: z.string().datetime({ offset: true }),
    source: memberCareAppointmentSourceSchema,
    /** When set, book with this specialist only (member follow-up on their profile). */
    specialistId: z.string().uuid().optional(),
    attribution: conversionAttributionSchema.optional(),
    durationMinutes: durationMinutesBodySchema,
    /**
     * Client-generated, one per booking intent and reused for every retry of
     * it: a request whose key this member already used returns that booking
     * instead of booking again. Optional so installed clients that predate it
     * keep booking exactly as before.
     */
    idempotencyKey: z.string().uuid().optional(),
    /** Book for this purpose instead of the next derived call; never with a specialist. */
    purpose: careBookingPurposeSchema.optional(),
  })
  .strict()
  .refine((request) => request.purpose === undefined || request.specialistId === undefined, {
    message: 'purpose cannot be combined with specialistId',
  });
export type BookCareAppointmentRequest = z.infer<typeof bookCareAppointmentRequestSchema>;

/**
 * Appointment projection returned by booking and upcoming-appointment reads.
 * Member surfaces render display fields only; staff Atlas scheduling may also
 * receive the governed `specialistClinicalRole` when the roster read failed.
 */
const appointmentTypeLabelKeySchema = z.string().regex(/^appointment\.type[A-Za-z]+$/);

/** A `appointment.type*` catalogue key; anything else reads as absent. */
const appointmentLabelKeySchema = z.preprocess((raw) => {
  if (raw === undefined) return undefined;
  const parsed = appointmentTypeLabelKeySchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}, appointmentTypeLabelKeySchema.optional());

export const careAppointmentDataSchema = z.object({
  id: z.string().uuid(),
  startsAt: z.string().datetime({ offset: true }),
  durationMinutes: z.number().int().positive(),
  /** Present when the server knows the booked call type; may be absent on
   * older rows, on reads that omit it, and on member reads of a call type
   * installed bundles cannot parse (`labelKey` labels those). */
  callType: tolerantRoutableCareCallTypeSchema,
  /** Staff reads only: the booking reason, when the call type carries one. */
  callReason: tolerantCareCallReasonSchema,
  /** Care catalogue key for the call's label, set when `callType` is omitted
   * for this audience; preferred over the label derived from `callType`. */
  labelKey: appointmentLabelKeySchema,
  /** False when the member may not move this call themselves (cancel still works). */
  memberCanReschedule: z.boolean().optional(),
  /** False when the member may not cancel this visit themselves (in person). */
  memberCanCancel: z.boolean().optional(),
  specialistName: z.string().min(1),
  /** Assigned specialist when the server knows it — scopes follow-up booking
   * from the care-team presentation seed without waiting for reconcile. */
  specialistId: z.string().uuid().optional(),
  specialistRole: z.string().min(1),
  /** Governed care-team role for the assigned specialist; absent when the
   * roster read did not surface a clinical role. Staff Atlas scheduling uses
   * it when the roster read failed but the booking write succeeded. */
  specialistClinicalRole: ClinicianRole.optional(),
  status: careAppointmentStatusSchema,
  /** Optional so a new client tolerates an old server that omits the field (ADR-135 Rule 6). */
  joinPromotedFrom: z.string().datetime({ offset: true }).optional(),
  /** The specialist's published photo; absent when they have none, so clients
   * fall back to initials. Optional for the same additive-compat reason. */
  specialistPhotoUrl: z
    .string()
    .regex(careTeamPhotoUrlShape, 'specialistPhotoUrl must be an https URL with a host')
    .optional(),
  /** Visit modality when stored; omitted on legacy-import null rows (ADR-135). */
  modality: careAppointmentModalitySchema.optional(),
});
export type CareAppointmentData = z.infer<typeof careAppointmentDataSchema>;

export const bookCareAppointmentResponseSchema = apiEnvelope(careAppointmentDataSchema);
export type BookCareAppointmentResponse = z.infer<typeof bookCareAppointmentResponseSchema>;

/**
 * Scope selector for `GET /api/v1/care/appointments`: `upcoming` (default)
 * returns live `booked` rows soonest first; `past` returns the member's call
 * history (completed + lapsed no-show bookings) newest first, capped
 * server-side. Both scopes share the list response shape below.
 */
export const careAppointmentListScopeSchema = z.enum(['upcoming', 'past']);
export type CareAppointmentListScope = z.infer<typeof careAppointmentListScopeSchema>;

/**
 * Response for `GET /api/v1/care/appointments`: every upcoming `booked`
 * appointment for the member (window not yet lapsed), soonest first. A member
 * may hold up to the service-enforced cap of upcoming appointments.
 */
export const careAppointmentListDataSchema = z
  .object({
    appointments: z.array(careAppointmentDataSchema),
  })
  .strict();
export type CareAppointmentListData = z.infer<typeof careAppointmentListDataSchema>;

export const careAppointmentListResponseSchema = apiEnvelope(careAppointmentListDataSchema);
export type CareAppointmentListResponse = z.infer<typeof careAppointmentListResponseSchema>;

/**
 * Request body for `POST /api/v1/care/appointments/reschedule` (member) and
 * the staff sibling on the telemedicine surface: move an existing `booked`
 * appointment to a new bookable instant. The response is the updated
 * appointment projection.
 */
export const rescheduleCareAppointmentRequestSchema = z
  .object({
    appointmentId: z.string().uuid(),
    startsAt: z.string().datetime({ offset: true }),
    durationMinutes: durationMinutesBodySchema,
  })
  .strict();
export type RescheduleCareAppointmentRequest = z.infer<
  typeof rescheduleCareAppointmentRequestSchema
>;

export const rescheduleCareAppointmentResponseSchema = apiEnvelope(careAppointmentDataSchema);
export type RescheduleCareAppointmentResponse = z.infer<
  typeof rescheduleCareAppointmentResponseSchema
>;

/**
 * Request body for the STAFF reschedule route only. `allowOutsideAvailability`
 * lets a specialist deliberately move a call to an instant outside their
 * published availability grid (member-side flexibility the member surface
 * never gets — the member request schema stays strict without this field).
 * Past instants and genuine double-bookings are still rejected server-side.
 * `specialistId` optionally re-points the appointment to another active
 * specialist at the same time as the move — omitted means the assignment
 * stays with the currently assigned specialist.
 */
export const staffRescheduleCareAppointmentRequestSchema = z
  .object({
    appointmentId: z.string().uuid(),
    startsAt: z.string().datetime({ offset: true }),
    allowOutsideAvailability: z.boolean().optional(),
    specialistId: z.string().uuid().optional(),
    durationMinutes: durationMinutesBodySchema,
  })
  .strict();
export type StaffRescheduleCareAppointmentRequest = z.infer<
  typeof staffRescheduleCareAppointmentRequestSchema
>;

/**
 * Request body for `POST /api/v1/internal/members/{ref}/care-appointments` —
 * a staff booking on the path-scoped member's behalf. `specialistId` omitted
 * books with the acting specialist themselves (never rotation);
 * `allowOutsideAvailability` mirrors the staff reschedule override. `source`
 * is server-set to `staff-atlas`, never carried on the wire.
 */
export const staffBookCareAppointmentRequestSchema = z
  .object({
    startsAt: z.string().datetime({ offset: true }),
    callType: routableCareCallTypeSchema,
    specialistId: z.string().uuid().optional(),
    allowOutsideAvailability: z.boolean().optional(),
    assignment: z.literal('round_robin').optional(),
    /**
     * Client-generated, one per booking intent and reused for every retry of
     * it: a request whose key this member already used returns that booking
     * instead of booking again. Optional so installed clients that predate it
     * keep booking exactly as before.
     */
    idempotencyKey: z.string().uuid().optional(),
    /** The booking reason: required for a P&P check-in (its variant), and only
     * ever one belonging to the booked call type. */
    callReason: careCallReasonSchema.optional(),
    modality: careAppointmentModalitySchema.optional(),
    durationMinutes: durationMinutesBodySchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.modality !== undefined && !isModalityAllowed(value.callType, value.modality)) {
      ctx.addIssue({
        code: 'custom',
        message: `modality ${value.modality} is not allowed for ${value.callType}`,
        path: ['modality'],
      });
    }
    if (
      isImportOnlyCareCallReason(value.callReason) ||
      !callReasonAllowedForCallType(value.callType, value.callReason)
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          value.callReason === undefined
            ? `callReason is required for ${value.callType}`
            : `callReason ${value.callReason} does not apply to ${value.callType}`,
        path: ['callReason'],
      });
    }
    if (value.assignment !== 'round_robin') return;
    if (value.specialistId !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'assignment round_robin cannot be combined with specialistId',
        path: ['specialistId'],
      });
    }
    if (value.allowOutsideAvailability === true) {
      ctx.addIssue({
        code: 'custom',
        message: 'assignment round_robin cannot be combined with allowOutsideAvailability',
        path: ['allowOutsideAvailability'],
      });
    }
  });
export type StaffBookCareAppointmentRequest = z.infer<typeof staffBookCareAppointmentRequestSchema>;

/** Query for `GET /api/v1/telemedicine/appointments/slots` (staff). */
export const telemedicineStaffSlotsQuerySchema = z
  .object({
    memberId: memberIdSchema,
    callType: routableCareCallTypeSchema.optional(),
    specialistId: z.string().uuid().optional(),
    scope: z.literal('pool').optional(),
    /** Booked call being moved — same slot semantics as the member reschedule read. */
    rescheduleAppointmentId: z.string().uuid().optional(),
    durationMinutes: durationMinutesQuerySchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.scope === 'pool' && value.specialistId !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'scope pool cannot be combined with specialistId',
        path: ['specialistId'],
      });
    }
  });
export type TelemedicineStaffSlotsQuery = z.infer<typeof telemedicineStaffSlotsQuerySchema>;

export const staffBookCareAppointmentResponseSchema = apiEnvelope(careAppointmentDataSchema);
export type StaffBookCareAppointmentResponse = z.infer<
  typeof staffBookCareAppointmentResponseSchema
>;

/**
 * One cached `(specialist, instant)` availability pair — the jsonb row shape
 * of `care_availability_slot_caches.slots` (ADR-066: jsonb content typed by
 * Zod). Mirrors the provider's `SpecialistInstant` without importing the
 * provider package into shared.
 */
export const careAvailabilityCachedSlotSchema = z
  .object({
    specialistId: z.string().uuid(),
    instant: z.string().datetime({ offset: true }),
  })
  .strict();
export type CareAvailabilityCachedSlot = z.infer<typeof careAvailabilityCachedSlotSchema>;

/** Request body for `POST /api/v1/care/appointments/cancel`. */
export const cancelCareAppointmentRequestSchema = z
  .object({
    appointmentId: z.string().uuid(),
  })
  .strict();
export type CancelCareAppointmentRequest = z.infer<typeof cancelCareAppointmentRequestSchema>;

export const cancelCareAppointmentDataSchema = z
  .object({
    id: z.string().uuid(),
    status: z.literal('cancelled'),
  })
  .strict();
export type CancelCareAppointmentData = z.infer<typeof cancelCareAppointmentDataSchema>;

export const cancelCareAppointmentResponseSchema = apiEnvelope(cancelCareAppointmentDataSchema);
export type CancelCareAppointmentResponse = z.infer<typeof cancelCareAppointmentResponseSchema>;

/** Stable error codes for the booking pipeline (ADR-002 envelope `error.code`). */
export const CARE_APPOINTMENT_ERROR_CODES = {
  invalidSlot: 'invalid-slot',
  slotTaken: 'slot-taken',
  /** @deprecated No longer emitted; kept for one release so installed clients keep parsing (ADR-135). */
  limitReached: 'booking-limit-reached',
  /** A booking reused an idempotency key for a different slot, specialist, source or call type. */
  idempotencyKeyReused: 'idempotency-key-reused',
  notFound: 'appointment-not-found',
  /** Book-time availability revalidation failed (provider outage) — retryable. */
  availabilityUnverifiable: 'availability-unverifiable',
  /** Healthie-wired typed booking with no policy appointment-type id — retryable after ops pairs. */
  healthieTypeMissing: 'healthie-type-missing',
  /** `durationMinutes` is not an option of the call type, or is staff-only on a member route. */
  durationNotAllowed: 'duration-not-allowed',
  /** Staff booking/reschedule named a specialist that does not exist. */
  specialistNotFound: 'specialist-not-found',
  /** Staff booking/reschedule named a specialist whose row is inactive. */
  specialistInactive: 'specialist-inactive',
  /** Licence-gated call type requested for a member with no stored US state. */
  memberStateUnknown: 'member-state-unknown',
  /** Provider call requested for a member with no medication-eligible programme. */
  medicationNotEligible: 'medication-not-eligible',
  /** Coverage toggle would leave a first-call type with no enabled specialist. */
  firstCallPoolEmpty: 'first_call_pool_empty',
  /** Clearing the flag would leave no active default-pool specialist to receive new members. */
  defaultPoolEmpty: 'default_pool_empty',
  /** Setting the flag on a specialist the default pool cannot pick (no open care_specialist grant, or inactive). */
  defaultPoolIneligible: 'default_pool_ineligible',
  /** A programme rule was submitted with no programme keys. */
  ruleEmpty: 'rule_empty',
  /** More than one rule of the same kind for one specialist and scope. */
  ruleDuplicateKind: 'rule_duplicate_kind',
  /** A programme rule named a programme key that is not in the active catalogue. */
  programmeUnknown: 'programme_unknown',
  /** Policy rows are seeded and PATCH-only — create/delete rejected. */
  callTypePolicyImmutable: 'call_type_policy_immutable',
  /** Manager write routes require the manager Cognito group claim. */
  coverageForbidden: 'coverage_forbidden',
  /** follow_up has no coverage matrix row — toggles are rejected. */
  followUpCoverageNotApplicable: 'follow_up_coverage_not_applicable',
  /** Specialist clinical role may not hold coverage for this call type. */
  coverageRoleIneligible: 'coverage_role_ineligible',
  /** The call type is staff-booked only, so a member may not move it themselves. */
  rescheduleNotAllowed: 'reschedule-not-allowed',
  /** Modality change requested while the appointment is not booked. */
  appointmentNotBooked: 'appointment_not_booked',
  /** Member attempted reschedule/cancel on an in-person visit. */
  modalityNotMemberChangeable: 'modality_not_member_changeable',
  /** The member asked for an HDHP booking the cost-share notice does not apply to. */
  hdhpBookingNotAllowed: 'hdhp_booking_not_allowed',
  /** The member already holds a booked HDHP cost-navigation call. */
  hdhpCallAlreadyBooked: 'hdhp_call_already_booked',
} as const;

/** Request body for `POST /api/v1/telemedicine/appointments/{appointmentId}/modality`. */
export const telemedicineAppointmentModalityRequestSchema = z
  .object({
    modality: careAppointmentModalitySchema,
  })
  .strict();
export type TelemedicineAppointmentModalityRequest = z.infer<
  typeof telemedicineAppointmentModalityRequestSchema
>;
export type CareAppointmentErrorCode =
  (typeof CARE_APPOINTMENT_ERROR_CODES)[keyof typeof CARE_APPOINTMENT_ERROR_CODES];

/**
 * Response for `GET /api/v1/care/appointments/{appointmentId}/details`: one
 * member-owned appointment with specialist presentation and relationship
 * counters. Non-strict on the wire (ADR-135) so forward-compatible fields may
 * arrive without breaking older clients.
 */
export const careAppointmentDetailsSpecialistSchema = z.object({
  name: z.string().min(1),
  firstName: z.string().min(1),
  clinicalRole: ClinicianRole.optional(),
  photoUrl: z.string().url().optional(),
});

export const careAppointmentDetailsRelationshipSchema = z.object({
  sessionsTogether: z.number().int().nonnegative(),
  sessionNumber: z.number().int().positive(),
  /** The member-local calendar date the pairing began (`YYYY-MM-DD`). */
  specialistSince: z.string().date().optional(),
});

export const careAppointmentDetailsDataSchema = z.object({
  id: z.string().uuid(),
  startsAt: z.string().datetime({ offset: true }),
  durationMinutes: z.number().int().positive(),
  callType: tolerantRoutableCareCallTypeSchema,
  labelKey: appointmentLabelKeySchema,
  memberCanReschedule: z.boolean().optional(),
  memberCanCancel: z.boolean().optional(),
  modality: careAppointmentModalitySchema.optional(),
  status: careAppointmentStatusSchema,
  specialist: careAppointmentDetailsSpecialistSchema,
  relationship: careAppointmentDetailsRelationshipSchema,
});

export type CareAppointmentDetailsData = z.infer<typeof careAppointmentDetailsDataSchema>;

export const careAppointmentDetailsResponseSchema = apiEnvelope(careAppointmentDetailsDataSchema);
export type CareAppointmentDetailsResponse = z.infer<typeof careAppointmentDetailsResponseSchema>;

/** Server-computed reward row for the Care tab and booking earn line (ADR-135 tolerant). */
export const careRewardPromoSchema = z.object({
  points: z.number().int().positive(),
  dollars: z.number().int().nonnegative(),
});
export type CareRewardPromo = z.infer<typeof careRewardPromoSchema>;

export const careOverviewSpecialistSchema = z.object({
  specialistId: z.string().uuid(),
  bio: z.string().min(1).nullable(),
});
export type CareOverviewSpecialist = z.infer<typeof careOverviewSpecialistSchema>;

export const careOverviewRewardPromosSchema = z.object({
  welcomeCall: careRewardPromoSchema.nullable(),
  careCall: careRewardPromoSchema.nullable(),
});
export type CareOverviewRewardPromos = z.infer<typeof careOverviewRewardPromosSchema>;

export const careOverviewQuerySchema = z
  .object({
    /**
     * The member's own booked call to take the specialist from — the matched
     * confirmation names the call it just booked, which may be held by someone
     * other than the member's primary.
     */
    appointmentId: z.string().uuid().optional(),
  })
  .strict();
export type CareOverviewQuery = z.infer<typeof careOverviewQuerySchema>;

export const careOverviewDataSchema = z.object({
  welcomeCallAttended: z.boolean(),
  /**
   * Whether intake is done by the server's intake rule: an attended, completed
   * welcome call, excluding not-attended and no-show rows. Optional so a client
   * keeps parsing an older server's overview (ADR-135).
   */
  intakeCompleted: z.boolean().optional(),
  specialist: careOverviewSpecialistSchema.nullable(),
  rewardPromos: careOverviewRewardPromosSchema,
});
export type CareOverviewData = z.infer<typeof careOverviewDataSchema>;

export const careOverviewResponseSchema = apiEnvelope(careOverviewDataSchema);
export type CareOverviewResponse = z.infer<typeof careOverviewResponseSchema>;

/** Query for `GET /api/v1/telemedicine/appointments/progress-purpose-defaults` (staff). */
export const progressPurposeDefaultsQuerySchema = z.object({ memberId: memberIdSchema }).strict();
export type ProgressPurposeDefaultsQuery = z.infer<typeof progressPurposeDefaultsQuerySchema>;

/** What the three-month default date was counted from. */
export const PROGRESS_PURPOSE_DEFAULT_BASIS = ['last_pnp', 'intake'] as const;
export const progressPurposeDefaultBasisSchema = z.enum(PROGRESS_PURPOSE_DEFAULT_BASIS);
export type ProgressPurposeDefaultBasis = z.infer<typeof progressPurposeDefaultBasisSchema>;

/**
 * Booking defaults for a member's next P&P check-in: the suggested variant, and
 * the default date (three months after the basis, start of that day in the
 * member's zone). All three date fields are null when the member has no
 * completed call to count from.
 */
export const progressPurposeDefaultsDataSchema = z.object({
  suggestedReason: progressPurposeReasonSchema,
  basis: progressPurposeDefaultBasisSchema.nullable(),
  basisDate: z.string().datetime({ offset: true }).nullable(),
  defaultDate: z.string().datetime({ offset: true }).nullable(),
});
export type ProgressPurposeDefaultsData = z.infer<typeof progressPurposeDefaultsDataSchema>;

export const progressPurposeDefaultsResponseSchema = apiEnvelope(progressPurposeDefaultsDataSchema);
export type ProgressPurposeDefaultsResponse = z.infer<typeof progressPurposeDefaultsResponseSchema>;
